import { Injectable, NgZone, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { filter, take } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
  ONETRUST_SCRIPT_ID,
  ONETRUST_SDK_STUB_URL,
} from 'src/app/core/constants/cookie-consent.constants';
import { MARKET } from 'src/app/core/constants/market.constants';
import { detectMarket } from 'src/app/core/utils/market.util';
import { CookieConsentService } from './cookie-consent.service';
import { resolveAnalyticsConsentFromDocument } from './onetrust-consent.util';
import { PendoAnalyticsService } from '../pendo/pendo-analytics.service';
import type { OneTrustConsentChangedEvent, OneTrustWindow } from './onetrust.types';

/** Re-read consent after OneTrust finishes writing cookies (groups can lag behind intType). */
const CONSENT_SYNC_RETRY_MS = 250;

/**
 * Loads the OneTrust CMP for the current market and mirrors its analytics-cookie decision
 * ({@link ONETRUST_ANALYTICS_CATEGORY}) into {@link CookieConsentService}, which gates Pendo.
 *
 * The CMP banner is loaded only AFTER the user authenticates, so consent is never requested on the
 * login page. If no `data-domain-script` id is configured for the market, this is a no-op and the
 * in-app fallback banner remains in charge of consent.
 */
@Injectable({ providedIn: 'root' })
export class OneTrustConsentService {
  private readonly consent = inject(CookieConsentService);
  private readonly auth = inject(AuthService);
  private readonly ngZone = inject(NgZone);
  private readonly pendo = inject(PendoAnalyticsService);

  private loaded = false;
  private lastConsentEventDetail: string[] | undefined;
  private consentSyncRetryTimer: ReturnType<typeof setTimeout> | undefined;

  /**
   * Registers the OneTrust loader (when a script id is configured). The stub script is only
   * injected once the user is authenticated, keeping the consent banner off the login page.
   */
  initialize(): void {
    if (this.loaded) {
      return;
    }

    const scriptId = this.resolveScriptId();
    if (!scriptId) {
      return;
    }

    this.loaded = true;
    // Suppress the in-app fallback banner up front; OneTrust owns consent once configured.
    this.consent.markExternalCmpActive();

    this.auth.isAuthenticated$
      .pipe(
        filter((isAuthenticated): isAuthenticated is boolean => isAuthenticated === true),
        take(1),
      )
      .subscribe(() => {
        this.installOptanonWrapper();
        this.injectStubScript(scriptId);
      });
  }

  private resolveScriptId(): string {
    const ids = environment.cookieConsent?.scriptId;
    const id = detectMarket() === MARKET.NZ ? ids?.nz : ids?.au;
    return (id ?? '').trim();
  }

  /**
   * Registers all consent listeners:
   * - `OptanonWrapper`: invoked by OneTrust on load and on change (cookie may lag on save).
   * - `OneTrustGroupsUpdated` window event: fires reliably on Accept/Reject with the freshly active
   *   groups in `detail`, independent of cookie/global timing — the primary in-session signal.
   */
  private installOptanonWrapper(): void {
    const win = globalThis.window as OneTrustWindow;
    win.OptanonWrapper = () => {
      this.ngZone.run(() => this.syncConsentFromOneTrust());
    };

    win.addEventListener('OneTrustGroupsUpdated', (event) => {
      const detail = (event as CustomEvent<string[] | string>).detail;
      this.lastConsentEventDetail = this.normalizeConsentEventDetail(detail);
      this.ngZone.run(() => this.syncConsentFromOneTrust());
    });
  }

  private normalizeConsentEventDetail(detail: string[] | string | undefined): string[] | undefined {
    if (Array.isArray(detail)) {
      return detail;
    }
    if (typeof detail === 'string') {
      return detail.split(',').filter(Boolean);
    }
    return undefined;
  }

  private injectStubScript(scriptId: string): void {
    if (document.getElementById(ONETRUST_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = ONETRUST_SCRIPT_ID;
    script.src = ONETRUST_SDK_STUB_URL;
    script.type = 'text/javascript';
    script.setAttribute('charset', 'UTF-8');
    script.dataset['domainScript'] = scriptId;
    script.async = true;
    script.onload = () => this.registerConsentChangeHandler();

    document.head.appendChild(script);
  }

  /** Prefer the explicit change event when available; also sync the initial state. */
  private registerConsentChangeHandler(): void {
    const win = globalThis.window as OneTrustWindow;
    win.OneTrust?.OnConsentChanged?.((event: OneTrustConsentChangedEvent) => {
      this.lastConsentEventDetail = event.detail;
      this.ngZone.run(() => {
        this.syncConsentFromOneTrust();
        this.scheduleConsentSyncRetry();
      });
    });

    this.syncConsentFromOneTrust();
  }

  private scheduleConsentSyncRetry(): void {
    if (this.consentSyncRetryTimer) {
      clearTimeout(this.consentSyncRetryTimer);
    }

    this.consentSyncRetryTimer = setTimeout(() => {
      this.consentSyncRetryTimer = undefined;
      this.lastConsentEventDetail = undefined;
      this.ngZone.run(() => this.syncConsentFromOneTrust());
    }, CONSENT_SYNC_RETRY_MS);
  }

  private syncConsentFromOneTrust(): void {
    const win = globalThis.window as OneTrustWindow;

    // Only act once OneTrust has initialized.
    if (!win.OneTrust) {
      return;
    }

    const decision = resolveAnalyticsConsentFromDocument({
      activeGroups: win.OnetrustActiveGroups,
      consentEventDetail: this.lastConsentEventDetail,
    });

    if (decision === 'granted') {
      this.consent.grantConsent();
      // Kick Pendo immediately in the current SPA session; do not wait for a page refresh.
      this.pendo.ensureTrackingInitialized();
      return;
    }

    if (decision === 'denied') {
      this.consent.denyConsent();
    }
  }
}
